import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Wand2, Loader2, Send, Trash2, RefreshCw, ExternalLink, MapPin, Sparkles, StopCircle, X, Eye, Star } from "lucide-react";
import api from "@/lib/api";
import CollapsibleCard from "@/components/CollapsibleCard";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Admin · Prospecting Panel — AI-generated cold outreach previews.
 *
 * Flow:
 * 1. Admin clicks "Start nieuwe scan" → backend kicks off an Apify Immoweb
 *    scrape, scores 5 listings' photos with Claude vision, generates 3
 *    AI-retouched versions per listing.
 * 2. Each result lands here as a pending_review draft with before/after pairs.
 * 3. Admin reviews → [Stuur door] sends the demo-outreach mail with the
 *    first pair inline + link to public gallery, or [Verwerp] soft-deletes.
 */

const STATUS_LABEL = {
    pending_review: { label: "Wacht op review", cls: "bg-amber-500/10 text-amber-700 border-amber-500/30" },
    edited: { label: "Bewerkt", cls: "bg-blue-500/10 text-blue-700 border-blue-500/30" },
    sent: { label: "Verzonden", cls: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" },
    rejected: { label: "Verworpen", cls: "bg-zinc-100 text-zinc-500 border-zinc-300" },
};

const ACTION_LABEL = {
    sunny: "Zonnig maken",
    declutter: "Opruimen",
    add_plants: "Planten erbij",
    relight: "Belichting",
    restyle: "Restyle",
    blur: "Blurren",
};

export function ProspectingPanel() {
    const [drafts, setDrafts] = useState([]);
    const [runs, setRuns] = useState([]);
    const [statusFilter, setStatusFilter] = useState("pending_review");
    const [loading, setLoading] = useState(true);
    const [scanning, setScanning] = useState(false);
    const [busyDraftId, setBusyDraftId] = useState(null);
    const [previewDraft, setPreviewDraft] = useState(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [d, r] = await Promise.all([
                api.get(`/admin/prospecting/drafts?status=${statusFilter}&limit=50`),
                api.get("/admin/prospecting/runs?limit=10"),
            ]);
            setDrafts(d.data.items || []);
            setRuns(r.data || []);
        } catch (e) {
            toast.error("Kon prospecting data niet laden");
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    // Initial load + reload when the status tab changes. Polling for active
    // runs is handled in a *separate* effect to avoid a re-render loop:
    // putting `runs` in this effect's deps would re-trigger loadData on every
    // fetch (setRuns always returns a new array ref → infinite reload + stuck
    // "Laden…" spinner).
    useEffect(() => {
        loadData();
    }, [loadData]);

    // Background polling — every 5s when a scan is active so the user sees
    // listings_seen + diagnostics tick up in real-time (each phase takes
    // minutes; longer intervals make it feel stuck). Idle → 30s.
    useEffect(() => {
        const anyActive = runs.some((r) => ["starting", "scraping", "processing"].includes(r.status));
        const interval = anyActive ? 5000 : 30000;
        const t = setInterval(() => {
            if (runs.some((r) => ["starting", "scraping", "processing"].includes(r.status))) {
                loadData();
            }
        }, interval);
        return () => clearInterval(t);
    }, [runs, loadData]);

    // Default to 1 — testing should be the cheap default; the user picks a
    // bigger batch (3/5/10) when they want a real run.
    const [maxDrafts, setMaxDrafts] = useState(1);
    // Deep scrape mode — fetches 200 listings instead of 75. Costs ~3× more
    // on Apify but is the rescue switch when normal mode keeps returning 0
    // new candidates because the makelaar pool has been "burned" already.
    const [deepScan, setDeepScan] = useState(false);
    const [region, setRegion] = useState("all");

    const startScan = async () => {
        setScanning(true);
        try {
            const { data } = await api.post("/admin/prospecting/scan-now", null, {
                params: {
                    max_drafts: maxDrafts,
                    min_age_days: 14,
                    max_age_days: 180,
                    scrape_depth: deepScan ? "deep" : "normal",
                    rescan_cooldown_days: 60,
                    region,
                },
            });
            toast.success(`Scan gestart (run ${data.run_id.slice(0, 8)}…). Dit duurt ${deepScan ? "6-12" : "3-6"} minuten.`);
            // Fast-poll the new run for ~10s so we catch INSTANT failures
            // (e.g. Apify 403 "Monthly usage hard limit exceeded" kills the
            // worker in ~250ms — slower than the regular 5s poll catches).
            // If we see status='failed' we surface the error toast right
            // away so the admin doesn't sit wondering.
            const runId = data.run_id;
            for (let i = 0; i < 10; i++) {
                await new Promise((r) => setTimeout(r, 1000));
                try {
                    const { data: status } = await api.get(`/admin/prospecting/runs/${runId}`);
                    if (status && status.status === "failed") {
                        toast.error(
                            `Scan mislukt: ${status.error || "onbekende fout"}`,
                            { duration: 10000 },
                        );
                        break;
                    }
                    if (status && ["scraping", "processing", "completed"].includes(status.status)) {
                        break;  // worker passed the fragile boot phase
                    }
                } catch { /* ignore poll errors */ }
            }
            await loadData();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kon scan niet starten");
        } finally {
            setScanning(false);
        }
    };

    const cancelRun = async (runId) => {
        if (!window.confirm("Weet je zeker dat je deze scan wil stoppen? Lopende Fal.ai-generaties stoppen direct (geld besparen).")) return;
        try {
            await api.post(`/admin/prospecting/runs/${runId}/cancel`);
            toast.success("Scan gestopt");
            await loadData();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kon scan niet stoppen");
        }
    };

    const sendDraft = async (draftId) => {
        setBusyDraftId(draftId);
        try {
            await api.post(`/admin/prospecting/drafts/${draftId}/send`);
            toast.success("Mail verzonden");
            setPreviewDraft(null);
            await loadData();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kon mail niet versturen");
        } finally {
            setBusyDraftId(null);
        }
    };

    const openPreview = (draft) => {
        setPreviewDraft(draft);
    };

    const rejectDraft = async (draftId) => {
        if (!window.confirm("Deze draft verwerpen?")) return;
        setBusyDraftId(draftId);
        try {
            await api.delete(`/admin/prospecting/drafts/${draftId}`);
            toast.success("Verworpen");
            await loadData();
        } catch (e) {
            toast.error("Kon draft niet verwerpen");
        } finally {
            setBusyDraftId(null);
        }
    };

    const activeRun = runs.find((r) => ["starting", "scraping", "processing"].includes(r.status));

    // Surface the most-recent FAILED run as a persistent banner so the
    // admin sees critical errors (Apify token revoked, monthly limit
    // exceeded, etc.) instead of wondering why "scan does nothing".
    const lastFailedRun = (runs || []).find((r) => r.status === "failed");

    return (
        <section className="mb-10" data-testid="prospecting-panel">
            {lastFailedRun && lastFailedRun.error && (
                <div
                    className="mb-5 p-4 rounded-sm border border-red-500/40 bg-red-500/[0.08]"
                    data-testid="prospecting-failed-banner"
                >
                    <div className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center shrink-0 mt-0.5">
                            <span className="text-red-700 text-sm font-bold">!</span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-[10px] uppercase tracking-[0.18em] text-red-700 font-bold mb-1">
                                Laatste scan mislukt
                            </p>
                            <p className="text-sm text-zinc-800 break-words">
                                {(lastFailedRun.error || "").replace(/token=[^&\s"']+/gi, "token=•••")}
                            </p>
                            {/Monthly usage hard limit|platform-feature-disabled|403/i.test(lastFailedRun.error || "") && (
                                <p className="mt-2 text-xs text-zinc-500 leading-relaxed">
                                    💡 Dit betekent meestal dat je <strong className="text-zinc-800">Apify-maandquota op is</strong>. Ga naar{" "}
                                    <a href="https://console.apify.com/billing" target="_blank" rel="noreferrer" className="text-[#E5A93D] underline underline-offset-2">
                                        console.apify.com/billing
                                    </a>{" "}
                                    en upgrade je plan of wacht tot het einde van de maand.
                                </p>
                            )}
                            {/InvalidIP|camoufox|proxy/i.test(lastFailedRun.error || "") && (
                                <p className="mt-2 text-xs text-zinc-500 leading-relaxed">
                                    💡 Dit is een tijdelijke <strong className="text-zinc-800">anti-bot fout</strong> aan de kant van Apify (residentiële proxy). We hebben automatisch <strong className="text-zinc-800">3× opnieuw geprobeerd</strong> met verschillende proxy-groepen. Wacht 5-10 min en start opnieuw — de camoufox IP-pool herstelt meestal snel.
                                </p>
                            )}
                            {Array.isArray(lastFailedRun.diagnostics?.attempts) && lastFailedRun.diagnostics.attempts.length > 0 && (
                                <details className="mt-3 text-xs text-zinc-500" data-testid="prospecting-failed-attempts">
                                    <summary className="cursor-pointer text-zinc-600 hover:text-zinc-800">
                                        Pogingen ({lastFailedRun.diagnostics.attempts.length})
                                    </summary>
                                    <ul className="mt-2 space-y-1 font-mono text-[11px] max-h-40 overflow-y-auto">
                                        {lastFailedRun.diagnostics.attempts.map((a, i) => (
                                            <li key={i} className="text-zinc-500">
                                                #{a.attempt} [{(a.proxy_groups || []).join(",") || "?"}] · {a.phase || "?"} · <span className={a.status === "SUCCEEDED" ? "text-emerald-600" : "text-red-600"}>{a.status || a.code || "—"}</span>
                                                {a.dutch_message && <span className="block text-zinc-500 ml-4">→ {a.dutch_message}</span>}
                                            </li>
                                        ))}
                                    </ul>
                                </details>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <CollapsibleCard
                storageKey="vsf_prospecting_open"
                testId="prospecting-collapse"
                title="AI Prospecting Engine"
                icon={<Wand2 className="h-4 w-4 text-[#E5A93D] shrink-0" />}
                badge={drafts.length || null}
                extra={activeRun ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-[#9A6E1B] font-bold">
                        <Loader2 className="h-3 w-3 animate-spin" /> scan bezig
                    </span>
                ) : null}
            >
            <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
                <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#E5A93D] mb-2 inline-flex items-center gap-2 font-bold">
                        <Wand2 className="h-3.5 w-3.5" /> AI Prospecting Engine
                    </p>
                    <h2 className="font-display text-2xl font-bold tracking-tight">Demo-mails klaarzetten</h2>
                    <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
                        Kies hoeveel panden u wil scrapen → Apify haalt nieuwe Belgische makelaars (URL-dedup blokkeert dubbele) →
                        u kiest zelf welke 3 foto&apos;s per pand AI-bewerkt worden in het &quot;Te selecteren panden&quot; paneel hieronder.
                        Daarna verschijnt de draft hier ter goedkeuring vóór de mail uitgaat.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={loadData}
                        data-testid="prospecting-refresh-btn"
                        className="inline-flex items-center gap-2 h-10 px-3 rounded-sm border border-zinc-200 text-zinc-600 hover:bg-zinc-100 text-xs"
                    >
                        <RefreshCw className="h-3.5 w-3.5" /> Vernieuw
                    </button>
                    <div className="inline-flex items-center h-10 rounded-sm border border-white/15 overflow-hidden bg-black/40" data-testid="prospecting-batch-size">
                        <span className="px-3 text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-bold border-r border-zinc-200 h-full inline-flex items-center">Aantal</span>
                        {[1, 3, 5, 10].map((n) => (
                            <button
                                key={n}
                                type="button"
                                onClick={() => setMaxDrafts(n)}
                                disabled={!!activeRun}
                                data-testid={`prospecting-batch-${n}`}
                                className={`h-full px-3 text-xs font-bold transition border-r border-zinc-200 last:border-r-0 ${
                                    maxDrafts === n
                                        ? "bg-[#E5A93D] text-black"
                                        : "text-zinc-600 hover:bg-zinc-100 disabled:opacity-50"
                                }`}
                            >
                                {n}
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={startScan}
                        disabled={scanning || !!activeRun}
                        data-testid="prospecting-start-scan-btn"
                        className="inline-flex items-center gap-2 h-10 px-4 rounded-sm bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold text-sm transition active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {scanning || activeRun ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                {activeRun ? `Bezig (${activeRun.status})…` : "Starten…"}
                            </>
                        ) : (
                            <>
                                <Sparkles className="h-4 w-4" /> Start nieuwe scan ({maxDrafts})
                            </>
                        )}
                    </button>
                </div>
                {/* Deep-scan toggle — escape hatch for when normal mode keeps
                    returning 0 fresh candidates. Costs more on Apify (~3×) so
                    it's opt-in only. */}
                <label
                    className="mt-3 flex items-center gap-2 text-xs text-zinc-500 cursor-pointer hover:text-zinc-800 transition"
                    data-testid="prospecting-deep-scan-label"
                >
                    <input
                        type="checkbox"
                        checked={deepScan}
                        onChange={(e) => setDeepScan(e.target.checked)}
                        data-testid="prospecting-deep-scan-toggle"
                        disabled={scanning || !!activeRun}
                        className="h-4 w-4 rounded-sm accent-[#E5A93D]"
                    />
                    <span>
                        <strong className="text-zinc-800">Diep scannen</strong>
                        <span className="text-zinc-500"> · 200 listings i.p.v. 75 — gebruik wanneer normaal 0 nieuwe panden oplevert (~3× duurder op Apify, 6-12 min)</span>
                    </span>
                </label>
                {/* Region selector — aim a scan at Wallonia/Brussels to grow
                    the French-speaking lead pool. Leads from wal/bru scans get
                    language "fr" automatically → FR drip mails. */}
                <label className="mt-2 flex items-center gap-2 text-xs text-zinc-500" data-testid="prospecting-region-label">
                    <span className="text-zinc-800 font-bold">Regio:</span>
                    <select
                        value={region}
                        onChange={(e) => setRegion(e.target.value)}
                        disabled={scanning || !!activeRun}
                        data-testid="prospecting-region-select"
                        className="h-8 px-2 rounded-sm border border-zinc-200 bg-white text-xs text-zinc-800"
                    >
                        <option value="all">Heel België (NL+FR)</option>
                        <option value="vl">Vlaanderen (NL)</option>
                        <option value="wal">Wallonië (FR)</option>
                        <option value="bru">Brussel (FR)</option>
                    </select>
                    <span className="text-zinc-400">Leads uit Wallonië/Brussel krijgen automatisch Franstalige mails</span>
                </label>
            </div>

            {/* Run-progress strip */}
            {activeRun && (
                <div className="mb-4 p-3 rounded-sm border border-[#E5A93D]/30 bg-[#E5A93D]/[0.06] text-sm" data-testid="prospecting-active-run">
                    <div className="flex items-center gap-3">
                        <Loader2 className="h-4 w-4 animate-spin text-[#E5A93D]" />
                        <div className="flex-1 min-w-0">
                            <div className="text-zinc-800">
                                Scan loopt — status: <strong className="text-[#E5A93D]">{activeRun.status}</strong>
                                <RunElapsed startedAt={activeRun.started_at || activeRun.created_at} />
                            </div>
                            <div className="text-zinc-500 text-xs mt-0.5">
                                listings gezien: {activeRun.listings_seen || 0} · drafts: {activeRun.drafts_created || 0}/{activeRun.max_drafts}
                                {activeRun.status === "scraping" && (
                                    <span className="ml-2 text-amber-400/80">· Apify scraped Immoweb (~3-8 min)</span>
                                )}
                                {activeRun.status === "processing" && (
                                    <span className="ml-2 text-amber-400/80">· Vision-scoring + Fal.ai retouches lopen</span>
                                )}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => cancelRun(activeRun.id)}
                            data-testid="prospecting-cancel-run-btn"
                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-sm border border-red-500/40 bg-red-500/10 text-red-700 hover:bg-red-500/20 text-xs font-semibold"
                        >
                            <StopCircle className="h-3.5 w-3.5" /> Stop scan
                        </button>
                    </div>
                    {activeRun.diagnostics && (
                        <div className="mt-3 pt-3 border-t border-zinc-200 text-[11px] text-zinc-500">
                            {/* Apify-level stats — surfaces immediately whether the scrape itself returned 0 listings
                                vs. whether candidates got filtered later. Critical for debugging "0 nieuwe panden". */}
                            {(activeRun.diagnostics.apify_run_status || activeRun.diagnostics.apify_dataset_fetched !== undefined) && (
                                <div className="mb-2 pb-2 border-b border-zinc-100 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1">
                                    <span><span className="text-zinc-500">Apify status:</span> <strong className={`${activeRun.diagnostics.apify_run_status === "SUCCEEDED" ? "text-emerald-700" : "text-amber-700"}`}>{activeRun.diagnostics.apify_run_status || "—"}</strong></span>
                                    <span><span className="text-zinc-500">Dataset items:</span> <strong className="text-zinc-800">{activeRun.diagnostics.apify_dataset_fetched ?? 0}</strong></span>
                                    <span><span className="text-zinc-500">Gevraagd:</span> <strong className="text-zinc-800">{activeRun.diagnostics.apify_oversample_requested ?? activeRun.diagnostics.apify_fetch_limit ?? "—"}</strong></span>
                                    <span><span className="text-zinc-500">Diepte:</span> <strong className="text-zinc-800">{activeRun.scrape_depth || "normal"}</strong></span>
                                </div>
                            )}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 mb-2">
                                <span><span className="text-zinc-500">Na leeftijd:</span> <strong className="text-zinc-800">{activeRun.diagnostics.listings_after_age ?? 0}</strong></span>
                                <span><span className="text-zinc-500">Met kantoor:</span> <strong className="text-zinc-800">{activeRun.diagnostics.listings_with_agency ?? 0}</strong></span>
                                <span><span className="text-zinc-500">Unieke kantoren:</span> <strong className="text-zinc-800">{activeRun.diagnostics.listings_unique_agency ?? 0}</strong></span>
                                <span><span className="text-zinc-500">Na URL-dedup:</span> <strong className="text-zinc-800">{activeRun.diagnostics.listings_after_url_dedup ?? 0}</strong></span>
                                <span><span className="text-zinc-500">Verwerkt:</span> <strong className="text-zinc-800">{activeRun.diagnostics.candidates_processed ?? 0}/{activeRun.diagnostics.candidates_picked ?? 0}</strong></span>
                                <span><span className="text-zinc-500">Vision-drops:</span> <strong className="text-zinc-800">{activeRun.diagnostics.vision_dropped_no_usable_photos ?? 0}</strong></span>
                                <span><span className="text-zinc-500">Retouch-fails:</span> <strong className="text-zinc-800">{activeRun.diagnostics.retouch_failures ?? 0}</strong></span>
                                <span><span className="text-zinc-500">Cooldown:</span> <strong className="text-zinc-800">{activeRun.diagnostics.cooldown_days ?? 60}d</strong></span>
                            </div>
                            {Array.isArray(activeRun.diagnostics.trace) && activeRun.diagnostics.trace.length > 0 && (
                                <details className="cursor-pointer" data-testid="prospecting-diag-trace">
                                    <summary className="text-zinc-500 hover:text-zinc-800">Per-kantoor log ({activeRun.diagnostics.trace.length})</summary>
                                    <ul className="mt-1.5 ml-3 space-y-0.5 text-zinc-500">
                                        {activeRun.diagnostics.trace.map((line, i) => (
                                            <li key={i} className="font-mono">· {line}</li>
                                        ))}
                                    </ul>
                                </details>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Status tabs */}
            <div className="flex items-center gap-1.5 mb-4 overflow-x-auto">
                {[
                    { key: "pending_review", label: "Te reviewen" },
                    { key: "sent", label: "Verzonden" },
                    { key: "rejected", label: "Verworpen" },
                    { key: "all", label: "Alle" },
                ].map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => setStatusFilter(t.key)}
                        data-testid={`prospecting-tab-${t.key}`}
                        className={`text-xs px-3 h-8 rounded-sm border transition-colors shrink-0 ${
                            statusFilter === t.key
                                ? "bg-[#E5A93D] text-black border-[#E5A93D] font-semibold"
                                : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="py-12 text-center text-sm text-zinc-500">
                    <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" /> Laden…
                </div>
            ) : drafts.length === 0 ? (
                <div className="py-12 text-center text-sm text-zinc-500 border border-zinc-200 rounded-sm bg-white">
                    Nog geen drafts {statusFilter !== "all" ? `met status "${statusFilter}"` : ""}.
                    {statusFilter === "pending_review" && (
                        <p className="mt-2 text-xs">Klik &quot;Start nieuwe scan&quot; om de eerste 5 demo&apos;s te genereren (~3-6 min).</p>
                    )}
                </div>
            ) : (
                <div className="space-y-4">
                    {drafts.map((draft) => (
                        <DraftCard
                            key={draft.id}
                            draft={draft}
                            onPreview={() => openPreview(draft)}
                            onReject={() => rejectDraft(draft.id)}
                            isBusy={busyDraftId === draft.id}
                        />
                    ))}
                </div>
            )}
            </CollapsibleCard>

            {previewDraft && (
                <PreviewModal
                    draft={previewDraft}
                    onClose={() => setPreviewDraft(null)}
                    onConfirmSend={() => sendDraft(previewDraft.id)}
                    isBusy={busyDraftId === previewDraft.id}
                />
            )}
        </section>
    );
}


function DraftCard({ draft, onPreview, onReject, isBusy }) {
    const meta = STATUS_LABEL[draft.status] || STATUS_LABEL.pending_review;
    const canAct = draft.status === "pending_review" || draft.status === "edited";
    const created = draft.created_at ? new Date(draft.created_at).toLocaleString("nl-BE", {
        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    }) : "";
    const pairs = draft.pairs || [];
    const [emailDraft, setEmailDraft] = useState(draft.agency_email || "");
    const [savingEmail, setSavingEmail] = useState(false);
    const [featuredIdx, setFeaturedIdx] = useState(
        Number.isInteger(draft.featured_pair_index) ? draft.featured_pair_index : 0
    );
    const [savingFeatured, setSavingFeatured] = useState(false);
    const [language, setLanguage] = useState(draft.language || "nl");
    const [savingLanguage, setSavingLanguage] = useState(false);
    const hasEmail = !!(emailDraft && emailDraft.includes("@"));

    const saveEmail = async () => {
        if (!hasEmail) return;
        setSavingEmail(true);
        try {
            await api.patch(`/admin/prospecting/drafts/${draft.id}`, { agency_email: emailDraft });
            toast.success("E-mail opgeslagen");
            draft.agency_email = emailDraft;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kon e-mail niet opslaan");
        } finally {
            setSavingEmail(false);
        }
    };

    const selectFeatured = async (idx) => {
        if (idx === featuredIdx || savingFeatured) return;
        const prev = featuredIdx;
        setFeaturedIdx(idx);
        setSavingFeatured(true);
        try {
            await api.patch(`/admin/prospecting/drafts/${draft.id}`, { featured_pair_index: idx });
            draft.featured_pair_index = idx;
            toast.success(`Foto ${idx + 1} wordt groot getoond in de mail`);
        } catch (e) {
            setFeaturedIdx(prev);
            toast.error(e.response?.data?.detail || "Kon foto-selectie niet opslaan");
        } finally {
            setSavingFeatured(false);
        }
    };

    const changeLanguage = async (newLang) => {
        if (newLang === language || savingLanguage) return;
        const prev = language;
        setLanguage(newLang);
        setSavingLanguage(true);
        try {
            await api.patch(`/admin/prospecting/drafts/${draft.id}`, { language: newLang });
            draft.language = newLang;
            toast.success(`Mail-taal: ${({ nl: "Nederlands", fr: "Français", en: "English" })[newLang]}`);
        } catch (e) {
            setLanguage(prev);
            toast.error(e.response?.data?.detail || "Kon taal niet opslaan");
        } finally {
            setSavingLanguage(false);
        }
    };

    return (
        <article
            className="border border-zinc-200 rounded-sm bg-white overflow-hidden"
            data-testid={`prospecting-draft-${draft.id}`}
        >
            <div className="p-4 border-b border-zinc-200 flex flex-wrap items-start gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-display font-bold text-lg truncate">{draft.agency_name || "—"}</h3>
                        <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-sm border ${meta.cls}`}>
                            {meta.label}
                        </span>
                    </div>
                    {/* Email row: editable when missing or admin wants to override */}
                    {canAct ? (
                        <div className="flex items-center gap-2 mb-1">
                            <input
                                type="email"
                                value={emailDraft}
                                onChange={(e) => setEmailDraft(e.target.value)}
                                placeholder="vul email-adres van makelaar in"
                                data-testid={`draft-email-${draft.id}`}
                                className="flex-1 max-w-md h-8 bg-white border border-zinc-200 rounded-sm px-2 text-xs focus:outline-none focus:border-[#E5A93D]/60"
                            />
                            {emailDraft !== (draft.agency_email || "") && hasEmail && (
                                <button
                                    type="button"
                                    onClick={saveEmail}
                                    disabled={savingEmail}
                                    data-testid={`draft-email-save-${draft.id}`}
                                    className="h-8 px-2 rounded-sm border border-zinc-200 text-zinc-800 hover:bg-zinc-100 text-[11px] disabled:opacity-50"
                                >
                                    {savingEmail ? "..." : "Opslaan"}
                                </button>
                            )}
                            {!hasEmail && (
                                <span className="text-[10px] text-amber-600">
                                    Vul e-mail in vóór verzending
                                </span>
                            )}
                        </div>
                    ) : (
                        <p className="text-xs text-zinc-500 mb-1 truncate">{draft.agency_email || "—"}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500">
                        {draft.city && (
                            <span className="inline-flex items-center gap-1">
                                <MapPin className="h-3 w-3" /> {draft.city}
                            </span>
                        )}
                        <span>· {created}</span>
                    </div>
                    {draft.listing_url && (
                        <a
                            href={draft.listing_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-[#E5A93D] hover:underline mt-1"
                        >
                            <ExternalLink className="h-3 w-3" /> {draft.listing_title || "Bekijk pand"}
                        </a>
                    )}
                </div>
                {canAct && (
                    <div className="flex items-center gap-2">
                        {/* Mail language pill selector */}
                        <div className="inline-flex items-center h-9 rounded-sm border border-white/15 overflow-hidden bg-black/40" data-testid={`draft-language-${draft.id}`}>
                            <span className="px-2 text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-bold border-r border-zinc-200 h-full inline-flex items-center" title="Taal van de e-mail">Taal</span>
                            {[
                                { code: "nl", flag: "🇧🇪" },
                                { code: "fr", flag: "🇫🇷" },
                                { code: "en", flag: "🇬🇧" },
                                { code: "es", flag: "🇪🇸" },
                            ].map((opt) => (
                                <button
                                    key={opt.code}
                                    type="button"
                                    onClick={() => changeLanguage(opt.code)}
                                    disabled={savingLanguage}
                                    data-testid={`draft-language-${draft.id}-${opt.code}`}
                                    className={`h-full px-2.5 text-xs font-bold transition border-r border-zinc-200 last:border-r-0 disabled:opacity-50 ${
                                        language === opt.code
                                            ? "bg-[#E5A93D] text-black"
                                            : "text-zinc-600 hover:bg-zinc-100"
                                    }`}
                                >
                                    <span aria-hidden className="mr-1">{opt.flag}</span>
                                    {opt.code.toUpperCase()}
                                </button>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={onReject}
                            disabled={isBusy}
                            data-testid={`prospecting-reject-${draft.id}`}
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-zinc-200 text-zinc-600 hover:bg-zinc-100 text-xs disabled:opacity-50"
                        >
                            <Trash2 className="h-3.5 w-3.5" /> Verwerp
                        </button>
                        <button
                            type="button"
                            onClick={onPreview}
                            disabled={isBusy || !hasEmail || emailDraft !== (draft.agency_email || "")}
                            data-testid={`prospecting-send-${draft.id}`}
                            title={!hasEmail ? "Vul eerst e-mail in" : (emailDraft !== (draft.agency_email || "") ? "Sla e-mail eerst op" : "Bekijk mail vóór verzending")}
                            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-sm bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold text-xs transition active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                            Stuur door
                        </button>
                    </div>
                )}
            </div>

            {/* Pairs grid — click a pair to make it the hero shown in the mail. */}
            {canAct && pairs.length > 1 && (
                <p className="px-4 pt-3 text-[11px] uppercase tracking-[0.14em] text-zinc-500">
                    Tik een foto aan om die groot in de mail te tonen
                </p>
            )}
            <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                {pairs.map((p, idx) => (
                    <BeforeAfterCard
                        key={idx}
                        pair={p}
                        index={idx}
                        isFeatured={idx === featuredIdx}
                        selectable={canAct}
                        savingFeatured={savingFeatured}
                        onSelect={() => selectFeatured(idx)}
                    />
                ))}
                {pairs.length === 0 && (
                    <div className="md:col-span-3 text-center text-xs text-zinc-500 py-6">
                        Geen voor/na paren gegenereerd voor deze draft.
                    </div>
                )}
            </div>
        </article>
    );
}


function BeforeAfterCard({ pair, index, isFeatured, selectable, savingFeatured, onSelect }) {
    const [view, setView] = useState("after");
    const actionLabel = ACTION_LABEL[pair.action_preset] || pair.action_preset || "AI";
    const score = pair.score?.overall_score;
    const reason = pair.score?.reason;
    const url = view === "after" ? pair.after_url : pair.before_url;
    return (
        <div
            className={`relative border rounded-sm overflow-hidden bg-zinc-50 transition-all ${
                isFeatured
                    ? "border-[#E5A93D] ring-2 ring-[#E5A93D]/40 shadow-[0_0_0_1px_rgba(229,169,61,0.4)]"
                    : "border-zinc-200"
            } ${selectable && !isFeatured ? "hover:border-[#E5A93D]/50 cursor-pointer" : ""}`}
            data-testid={`pair-card-${index}`}
            data-featured={isFeatured ? "true" : "false"}
            onClick={(e) => {
                // Don't react to clicks on the Voor/Na toggle inside the card.
                if (!selectable) return;
                if (e.target.closest("[data-no-feature-select]")) return;
                onSelect();
            }}
        >
            {isFeatured && (
                <div className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 bg-[#E5A93D] text-black text-[10px] uppercase tracking-[0.1em] font-bold px-2 h-6 rounded-sm shadow">
                    <Star className="h-3 w-3 fill-current" /> In mail
                </div>
            )}
            {!isFeatured && selectable && (
                <div className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 bg-black/70 text-white text-[10px] uppercase tracking-[0.1em] font-semibold px-2 h-6 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity">
                    Kies voor mail
                </div>
            )}
            <div className="aspect-[4/3] bg-zinc-50 relative">
                {url && (
                    <img
                        src={`${BACKEND}${url}`}
                        alt={view}
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                )}
                <div className="absolute top-2 left-2 flex gap-1" data-no-feature-select="">
                    {["before", "after"].map((v) => (
                        <button
                            key={v}
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setView(v); }}
                            data-testid={`pair-toggle-${v}`}
                            className={`text-[10px] uppercase tracking-[0.1em] px-2 h-6 rounded-sm font-bold transition ${
                                view === v
                                    ? "bg-white text-zinc-950"
                                    : "bg-black/60 text-white hover:bg-black/80"
                            }`}
                        >
                            {v === "before" ? "Voor" : "Na"}
                        </button>
                    ))}
                </div>
                <div className="absolute bottom-2 right-2 text-[10px] uppercase tracking-[0.1em] bg-[#E5A93D] text-black px-2 h-6 rounded-sm font-bold inline-flex items-center">
                    {actionLabel}
                </div>
                {savingFeatured && isFeatured && (
                    <div className="absolute inset-0 bg-black/30 grid place-items-center">
                        <Loader2 className="h-5 w-5 animate-spin text-[#E5A93D]" />
                    </div>
                )}
            </div>
            {(reason || typeof score === "number") && (
                <div className="px-3 py-2 text-xs">
                    {typeof score === "number" && (
                        <div className="text-[10px] uppercase tracking-[0.1em] text-[#E5A93D] mb-0.5">
                            Score {score}/100
                        </div>
                    )}
                    {reason && <div className="text-zinc-600 line-clamp-2">{reason}</div>}
                </div>
            )}
        </div>
    );
}

export default ProspectingPanel;


function RunElapsed({ startedAt }) {
    const [elapsed, setElapsed] = useState(0);
    useEffect(() => {
        if (!startedAt) return undefined;
        const t0 = new Date(startedAt).getTime();
        const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - t0) / 1000)));
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [startedAt]);
    if (!startedAt) return null;
    const mm = Math.floor(elapsed / 60);
    const ss = elapsed % 60;
    return (
        <span className="ml-2 text-xs text-zinc-500 tabular-nums" data-testid="run-elapsed">
            · {mm}:{String(ss).padStart(2, "0")}
        </span>
    );
}


function PreviewModal({ draft, onClose, onConfirmSend, isBusy }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const { data } = await api.get(`/admin/prospecting/drafts/${draft.id}/preview`);
                if (alive) setPreview(data);
            } catch (e) {
                if (alive) setError(e.response?.data?.detail || "Kon preview niet laden");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [draft.id]);

    // Lock body scroll while modal is open
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, []);

    return (
        <div
            className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            data-testid="prospecting-preview-modal"
        >
            <div className="w-full max-w-4xl max-h-[92vh] flex flex-col bg-white border border-zinc-200 rounded-sm shadow-2xl">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 p-4 border-b border-zinc-200">
                    <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-[0.18em] text-[#E5A93D] mb-1 font-bold">
                            Mail-preview voor verzending
                        </p>
                        <h3 className="font-display text-lg font-bold truncate" data-testid="preview-modal-title">
                            {preview?.subject || draft.agency_name || "Mail-preview"}
                        </h3>
                        {preview?.to_email && (
                            <p className="text-xs text-zinc-500 mt-0.5">
                                Naar: <span className="text-zinc-800">{preview.to_email}</span>
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="preview-modal-close"
                        className="shrink-0 h-9 w-9 grid place-items-center rounded-sm border border-zinc-200 text-zinc-600 hover:bg-zinc-100"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {/* Body — iframe with srcDoc so the rendered HTML is fully isolated. */}
                <div className="flex-1 min-h-0 bg-white relative overflow-hidden">
                    {loading && (
                        <div className="absolute inset-0 grid place-items-center text-sm text-zinc-500 bg-white">
                            <Loader2 className="h-5 w-5 animate-spin" />
                        </div>
                    )}
                    {error && !loading && (
                        <div className="absolute inset-0 grid place-items-center text-sm text-red-600 bg-white px-6 text-center">
                            {error}
                        </div>
                    )}
                    {preview?.body_html && !loading && !error && (
                        <iframe
                            title="mail-preview"
                            srcDoc={preview.body_html}
                            sandbox="allow-same-origin"
                            className="w-full h-full border-0"
                            data-testid="preview-modal-iframe"
                        />
                    )}
                </div>

                {/* Footer */}
                <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-t border-zinc-200 bg-white">
                    <p className="text-[11px] text-zinc-500">
                        Controleer onderwerp, foto&apos;s en watermerken. Verzending kan niet ongedaan worden gemaakt.
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            data-testid="preview-modal-cancel"
                            disabled={isBusy}
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-zinc-200 text-zinc-600 hover:bg-zinc-100 text-xs disabled:opacity-50"
                        >
                            Annuleer
                        </button>
                        <button
                            type="button"
                            onClick={onConfirmSend}
                            data-testid="preview-modal-confirm-send"
                            disabled={isBusy || loading || !!error}
                            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-sm bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold text-xs transition active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                            Verstuur nu
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
